import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

type SidebarLink = {
  to: string;
  label: string;
  icon: string;
  live?: boolean;
  badgeAgents?: boolean;
  badgeNew?: boolean;
  badgeBeta?: boolean;
  /** When set with badgeBeta, styles the Beta pill (e.g. purple for Campaign Simulator) */
  badgeColor?: string;
};

const links: SidebarLink[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '🏠', live: true },
  { to: '/agents', label: 'AI Agents', icon: '🤖', badgeAgents: true },
  { to: '/content-generator', label: 'AI Content Gen', icon: '✍️', badgeNew: true },
  { to: '/agent-ops', label: 'Agent Operations', icon: '⚡', badgeNew: true },
  { to: '/leads', label: 'Leads', icon: '👥' },
  { to: '/calendar', label: 'Content Calendar', icon: '📅' },
  { to: '/calls', label: 'AI Calls', icon: '📞' },
  { to: '/analytics', label: 'Analytics', icon: '📊' },
  { to: '/webbuilder', label: 'WebBuilder', icon: '🌐', badgeNew: true },
  { to: '/onboarding-report', label: 'My ODC Report', icon: '📊' },
  { to: '/geo', label: 'GEO Score', icon: '📊', badgeBeta: true },
  {
    to: '/simulator',
    label: 'Campaign Simulator',
    icon: '🧪',
    badgeBeta: true,
    badgeColor: '#8B5CF6',
  },
  { to: '/whatsapp-commerce', label: 'WhatsApp Commerce', icon: '💬', badgeNew: true },
  { to: '/festive', label: 'Festive AI', icon: '🎉', badgeNew: true },
  { to: '/bharat-voice', label: 'Bharat Voice', icon: '🎙️', badgeNew: true },
  { to: '/brand-monitor', label: 'Brand Monitor', icon: '🛡️', badgeNew: true },
  { to: '/nri-marketing', label: 'NRI Marketing', icon: '🌍', badgeNew: true },
  { to: '/agentic-ads', label: 'Agentic Ads', icon: '📊', badgeNew: true },
  { to: '/app-builder', label: 'App Builder', icon: '📱', badgeNew: true },
  { to: '/marketplace', label: 'Marketplace', icon: '🏪', badgeNew: true },
  { to: '/blockchain-trust', label: 'Trust Certs', icon: '🔗', badgeNew: true },
  { to: '/faq', label: 'FAQ & Help', icon: '❓' },
  { to: '/support', label: 'Support Tickets', icon: '🎫', badgeNew: true },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

export function Sidebar({
  collapsed,
  onToggle,
  isDesktop = true,
  mobileOpen = false,
  onMobileClose,
}: {
  collapsed: boolean;
  onToggle: () => void;
  /** When false, sidebar is an off-canvas drawer controlled by mobileOpen */
  isDesktop?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [planLabel, setPlanLabel] = useState<string>('Starter');
  const [rawPlanName, setRawPlanName] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{
          stats: { subscription_plan_name: string | null };
        }>('/dashboard/stats');
        const n = data.stats.subscription_plan_name;
        setPlanLabel(n ? `${n}` : 'Starter');
        setRawPlanName(n);
      } catch {
        setPlanLabel('—');
        setRawPlanName(null);
      }
    })();
  }, []);

  const showUpgradeBanner =
    rawPlanName != null &&
    ['starter', 'growth'].includes(rawPlanName.trim().toLowerCase());

  const navTransformClass =
    isDesktop ? '' : mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none';

  const closeIfMobile = () => {
    if (!isDesktop) onMobileClose?.();
  };

  return (
    <aside
      className={`odc-sidebar odc-sidebar-shell fixed left-0 top-0 z-[60] flex h-screen shrink-0 flex-col border-r py-5 transition-transform duration-300 ease-out lg:z-50 lg:translate-x-0 lg:pointer-events-auto ${collapsed ? 'collapsed' : ''} ${navTransformClass}`}
      style={{
        background: 'color-mix(in srgb, var(--bg-primary) 94%, transparent)',
        borderColor: 'var(--border)',
        backdropFilter: 'blur(20px)',
        boxShadow: '4px 0 32px rgba(0,0,0,0.25)',
      }}
    >
      <button
        type="button"
        onClick={() => {
          if (isDesktop) onToggle();
          else onMobileClose?.();
        }}
        className="absolute -right-3 top-6 z-[5] flex h-6 w-6 items-center justify-center rounded-full border transition"
        style={{
          background: 'var(--bg-secondary)',
          borderColor: 'var(--border-accent)',
          color: 'var(--accent-2)',
          boxShadow: 'var(--shadow-gold)',
        }}
        aria-label={
          isDesktop ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : 'Close menu'
        }
      >
        <ChevronLeft
          size={14}
          style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
        />
      </button>

      <div className="odc-sidebar-scan" aria-hidden>
        <div className="odc-sidebar-scan-inner" />
      </div>

      <Link
        to="/dashboard"
        className="relative z-[3] mb-5 flex items-center gap-3 px-4"
        onClick={closeIfMobile}
      >
        <span
          className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-black tracking-tight ring-2 ring-[rgba(8,253,237,0.25)]"
          style={{
            background: 'var(--gradient-main)',
            color: '#1a1a12',
            boxShadow: 'var(--shadow-gold)',
          }}
        >
          O
        </span>
        <div className="sidebar-wordmark min-w-0 leading-tight">
          <div className="truncate text-sm font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            OnlineDigitalCares
          </div>
          <span className="odc-badge odc-badge-cyan mt-1">{planLabel}</span>
        </div>
      </Link>

      <nav
        id="odc-sidebar-nav"
        className="sidebar-nav-scroll relative z-[3] flex flex-1 flex-col px-2"
      >
        {links.map((l) => {
          const active = loc.pathname === l.to;
          return (
            <Link
              key={l.to}
              to={l.to}
              className={`odc-nav-item ${active ? 'active' : ''}`}
              title={isDesktop && collapsed ? l.label : undefined}
              aria-current={active ? 'page' : undefined}
              onClick={closeIfMobile}
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base"
                style={{
                  background: active ? 'rgba(8, 253, 237, 0.15)' : 'var(--bg-card)',
                  border: '1px solid var(--border)',
                }}
              >
                {l.icon}
              </span>
              <span className="sidebar-label min-w-0 flex-1 break-words leading-snug" title={l.label}>
                {l.label}
              </span>
              {!collapsed && 'live' in l && l.live && active ? (
                <span className="flex items-center gap-1">
                  <span className="odc-live-dot" />
                </span>
              ) : null}
              {!collapsed && 'badgeAgents' in l && l.badgeAgents ? (
                <span className="odc-live-dot shrink-0" title="Fleet connected" aria-hidden />
              ) : null}
              {!collapsed && 'badgeNew' in l && l.badgeNew ? (
                <span
                  className="shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
                  style={{ background: 'rgba(8,253,237,0.15)', color: 'var(--accent-2)' }}
                >
                  New
                </span>
              ) : null}
              {!collapsed && l.badgeBeta ? (
                <span
                  className="shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
                  style={{
                    background: l.badgeColor ? `${l.badgeColor}28` : 'rgba(6,148,148,0.15)',
                    color: l.badgeColor || '#069494',
                  }}
                >
                  Beta
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>

      <div
        className="relative z-[3] mt-auto border-t px-3 pt-4"
        style={{ borderColor: 'var(--border)' }}
      >
        <a
          href="mailto:support@onlinedigitalcares.com"
          className="odc-nav-item !mb-2"
          style={{ fontSize: 12 }}
          onClick={closeIfMobile}
        >
          <span className="text-base">❓</span>
          <span className="sidebar-label">Help &amp; Support</span>
        </a>
        {!collapsed && showUpgradeBanner ? (
          <div
            className="odc-card mb-3 !rounded-xl !p-3"
            style={{
              transform: 'none',
              boxShadow: 'none',
              borderColor: 'rgba(190,81,3,0.35)',
              background: 'linear-gradient(135deg, rgba(190,81,3,0.12), var(--card))',
            }}
          >
            <p className="text-xs font-bold" style={{ color: 'var(--text)' }}>
              🚀 Upgrade to Pro
            </p>
            <p className="mt-1 text-[10px] leading-snug" style={{ color: 'var(--muted)' }}>
              3x more agents + AI calling
            </p>
            <button
              type="button"
              className="odc-btn-primary mt-2 w-full !py-2 !text-[11px]"
              onClick={() => {
                closeIfMobile();
                nav('/pricing');
              }}
            >
              Upgrade
            </button>
          </div>
        ) : null}
        <div
          className="odc-card flex items-center gap-3 !rounded-xl p-3"
          style={{ boxShadow: 'none', transform: 'none' }}
        >
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold"
            style={{
              background: 'var(--gradient-main)',
              color: '#1a1a12',
              boxShadow: 'var(--shadow-gold)',
            }}
          >
            {(user?.name || user?.mobile || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="sidebar-profile-text min-w-0 flex-1">
            <p className="truncate text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              {user?.name || 'Account'}
            </p>
            <p
              className="mt-0.5 inline-block truncate rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
              style={{
                background: 'rgba(8, 253, 237, 0.15)',
                color: 'var(--accent-2)',
                border: '1px solid var(--border-accent)',
              }}
            >
              {user?.mobile || 'Account'}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="odc-btn-ghost mt-3 w-full justify-center !py-2.5 text-xs"
          onClick={() => {
            logout();
            window.location.href = '/login';
          }}
        >
          Log out
        </button>
      </div>
    </aside>
  );
}
